import React from 'react';
import { University } from '../../../types';

interface AcademicTabProps {
  university: University;
}

const AcademicTab: React.FC<AcademicTabProps> = ({ university }) => {
  // Format faculty-student ratio
  const formatFacultyRatio = (ratio: number | null | undefined) => {
    if (!ratio) return 'N/A';
    return `1:${ratio}`;
  };

  // Format online courses
  const formatOnlineCourses = (courses: string | null | undefined) => {
    if (!courses) return 'N/A';
    return courses;
  };

  return (
    <div className="tab-content">
      <h3 className="text-3xl font-bold mb-8" style={{ color: 'var(--text-primary)' }}>Academic Life</h3>
      
      {/* Academic Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        {/* Faculty-Student Ratio Card */}
        <div className="bg-blue-50/50 rounded-xl p-6">
          <div className="flex items-center mb-4">
            <div className="text-blue-500 text-2xl mr-3">
              <i className="fas fa-users"></i>
            </div>
            <h4 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>Faculty-Student Ratio</h4>
          </div>
          <div className="text-center">
            <p className="text-3xl font-bold text-blue-600">{formatFacultyRatio(university.student_faculty_ratio)}</p>
            <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>Personalized attention</p>
          </div>
        </div>

        {/* Online Courses Card */}
        <div className="bg-green-50/50 rounded-xl p-6">
          <div className="flex items-center mb-4">
            <div className="text-green-500 text-2xl mr-3">
              <i className="fas fa-laptop"></i>
            </div>
            <h4 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>Online Courses</h4>
          </div>
          <div className="text-center">
            <p className="text-3xl font-bold text-green-600">{formatOnlineCourses(university.offers_online_courses)}</p>
            <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>Flexible learning options</p>
          </div>
        </div>
      </div>

      {/* Academic Programs Section */}
      <div className="border rounded-xl p-6 mb-8" style={{ background: 'var(--surface-primary)', borderColor: 'var(--border-primary)' }}>
        <div className="flex items-center mb-6">
          <div className="text-purple-500 text-2xl mr-3">
            <i className="fas fa-graduation-cap"></i>
          </div>
          <h4 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>Academic Excellence</h4>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center p-4 bg-purple-50/50 rounded-lg">
            <h5 className="font-semibold mb-2" style={{ color: 'var(--text-secondary)' }}>Undergraduate Programs</h5>
            <p className="text-2xl font-bold text-purple-600">90+</p>
            <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>Majors available</p>
          </div>
          <div className="text-center p-4 bg-purple-50/50 rounded-lg">
            <h5 className="font-semibold mb-2" style={{ color: 'var(--text-secondary)' }}>Graduate Programs</h5>
            <p className="text-2xl font-bold text-purple-600">200+</p>
            <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>Advanced degrees</p>
          </div>
          <div className="text-center p-4 bg-purple-50/50 rounded-lg">
            <h5 className="font-semibold mb-2" style={{ color: 'var(--text-secondary)' }}>Research Centers</h5>
            <p className="text-2xl font-bold text-purple-600">18</p>
            <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>Interdisciplinary institutes</p>
          </div>
        </div>
      </div>

      {/* Hot Majors Section */}
      <div className="border rounded-xl p-6 mb-8" style={{ background: 'var(--surface-primary)', borderColor: 'var(--border-primary)' }}>
        <div className="flex items-center mb-6">
          <div className="text-red-500 text-2xl mr-3">
            <i className="fas fa-fire"></i>
          </div>
          <h4 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>Hot Majors</h4>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="text-center p-4 bg-red-50/50 rounded-lg">
            <h5 className="font-semibold mb-2" style={{ color: 'var(--text-secondary)' }}>Computer Science</h5>
            <p className="text-lg font-bold text-red-600">Most Popular</p>
            <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>AI & Machine Learning</p>
          </div>
          <div className="text-center p-4 bg-red-50/50 rounded-lg">
            <h5 className="font-semibold mb-2" style={{ color: 'var(--text-secondary)' }}>Engineering</h5>
            <p className="text-lg font-bold text-red-600">High Demand</p>
            <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>Biomedical & Electrical</p>
          </div>
          <div className="text-center p-4 bg-red-50/50 rounded-lg">
            <h5 className="font-semibold mb-2" style={{ color: 'var(--text-secondary)' }}>Business</h5>
            <p className="text-lg font-bold text-red-600">Growing Fast</p>
            <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>Entrepreneurship Focus</p>
          </div>
        </div>
      </div>

      {/* Academic Highlights */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
        <div className="flex items-start">
          <div className="text-blue-500 text-xl mr-3 mt-1">
            <i className="fas fa-star"></i>
          </div>
          <div>
            <h4 className="text-lg font-bold text-blue-900 mb-3">Academic Highlights</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-blue-800">
              <div>
                <p className="font-semibold mb-2">Teaching Excellence:</p>
                <ul className="space-y-1 text-sm">
                  <li>• Small class sizes with {formatFacultyRatio(university.student_faculty_ratio)} ratio</li>
                  <li>• Nobel Prize-winning faculty</li>
                  <li>• Hands-on research opportunities</li>
                </ul>
              </div>
              <div>
                <p className="font-semibold mb-2">Flexible Learning:</p>
                <ul className="space-y-1 text-sm">
                  <li>• Online course options available</li>
                  <li>• Study abroad programs</li>
                  <li>• Interdisciplinary collaboration</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AcademicTab;
